import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from "randmarcomps";
import WithManufacturer from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import CurrencyUtils from "../util/CurrencyUtils";
import CenteredLoader from "../util/CenteredLoader";

interface IProps {
  store?: RootStore;
  applicationId: string;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getDistributionTotal = (distribution: unknown): number => {
  if (!distribution) return 0;

  const anyDistribution = distribution as {
    total?: unknown;
    Inventory?: unknown;
    inventory?: unknown;
  };

  if (typeof anyDistribution.total === "function") {
    return (anyDistribution.total as () => number)();
  }

  const inventory = anyDistribution.Inventory ?? anyDistribution.inventory;
  if (!Array.isArray(inventory)) return 0;

  return inventory.reduce((sum, item) => {
    const anyItem = item as { AvailableQuantity?: number; availableQuantity?: number } | null | undefined;
    return sum + (anyItem?.AvailableQuantity ?? anyItem?.availableQuantity ?? 0);
  }, 0);
};

function EditStandardCost({ store, applicationId, product, open, onOpenChange }: IProps) {
  const [loading, setLoading] = React.useState(false);
  const [standardCost, setStandardCost] = React.useState(product.distribution?.cost || 0);

  React.useEffect(() => {
    if (open) {
      setStandardCost(product.distribution?.cost || 0);
    }
  }, [open, product.distribution?.cost]);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleConfirmRequest = React.useCallback(async () => {
    if (!store) {
      return;
    }

    setLoading(true);
    const { snackStore, productStore } = store;

    const success = await productStore.setProductProperty(applicationId, product.randmarSKU || "", "StandardCost", standardCost?.toString());
    if (success) snackStore.showResult(success);
    setLoading(false);
    onOpenChange(false);

    await productStore.getProduct(applicationId, product.randmarSKU || "");
  }, [applicationId, product.randmarSKU, store, standardCost, onOpenChange]);

  const propertyChange = (newValue: string) => {
    if (new RegExp("^[0-9]*(\\.[0-9]{0,2})?$").test(newValue)) {
      setStandardCost(newValue === "" ? NaN : parseFloat(newValue));
    }
  };

  const currentStandardCost = product.distribution?.cost || 0;
  const inventoryQuantity = React.useMemo(() => getDistributionTotal(product.distribution), [product.distribution]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle>Edit Standard Cost</DialogTitle>
        </DialogHeader>
        {loading ? (
          <CenteredLoader text="Please wait…" variant="plain" className="min-h-[240px]" />
        ) : (
          <>
            <div className="grid gap-4 py-4">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground">$</span>
                <Input
                  id="product-Cost"
                  placeholder="Edit cost"
                  value={isNaN(standardCost) ? "" : standardCost}
                  onChange={e => propertyChange(e.target.value)}
                  autoComplete="off"
                  type="number"
                  className="pl-7"
                />
              </div>

              {standardCost < currentStandardCost && inventoryQuantity > 0 && (
                <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200 text-sm">
                  <h3 className="font-semibold text-yellow-900">Inventory Protection</h3>
                  <div className="mt-2 space-y-1 text-yellow-800">
                    <div className="flex justify-between">
                      <span>Price difference</span>
                      <span><FormattedNumber value={currentStandardCost - standardCost} {...CurrencyUtils.format} /></span>
                    </div>
                    <div className="flex justify-between">
                      <span>Inventory quantity</span>
                      <span>{inventoryQuantity}</span>
                    </div>
                    <div className="flex justify-between font-bold">
                      <span>Expected credit</span>
                      <span><FormattedNumber value={(currentStandardCost - standardCost) * inventoryQuantity} {...CurrencyUtils.format} /></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleCancel} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRequest}
                disabled={isNaN(standardCost) || standardCost <= 0 || currentStandardCost === standardCost}
                variant={standardCost < currentStandardCost ? "destructive" : "default"}
              >
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default WithManufacturer(inject("store")(observer(EditStandardCost)));
